import {
  faBookOpen,
  faCalendarAlt,
  faCamera,
  faChartArea,
  faChartPie,
  faCheckCircle,
  faCog,
  faFish,
  faGlobe,
  faList,
  faMapMarkedAlt,
  faPlay,
  faProjectDiagram,
  faSitemap,
  faVideo,
  faVirus,
} from "@fortawesome/free-solid-svg-icons";
import type { IconDefinition } from "@fortawesome/fontawesome-common-types";

export interface ToolbarMeta {
  icon: IconDefinition;
  title: string;
  withDates?: boolean;
  hideSitePicker?: boolean;
  isSingleSite?: boolean;
  hide?: boolean;
}

const STATIC_ROUTE_META: Array<{ test: RegExp; meta: ToolbarMeta }> = [
  // Tide pages — mirror tide source <Toolbar> calls
  {
    test: /^\/welcome$/,
    meta: { icon: faBookOpen, title: "Welcome", hide: true },
  },
  {
    test: /^\/tide\/map(\/|$)/,
    meta: {
      icon: faMapMarkedAlt,
      title: "Map",
      withDates: true,
      hideSitePicker: true,
      isSingleSite: false,
    },
  },
  {
    test: /^\/tide\/goals(\/|$)/,
    meta: {
      icon: faCheckCircle,
      title: "Goals",
      withDates: true,
      isSingleSite: true,
    },
  },
  {
    test: /^\/tide\/calendar(\/|$)/,
    meta: {
      icon: faCalendarAlt,
      title: "Calendar",
      withDates: false,
      isSingleSite: true,
    },
  },
  {
    test: /^\/tide\/biolife(\/|$)/,
    meta: {
      icon: faVirus,
      title: "Biolife",
      withDates: true,
      isSingleSite: true,
    },
  },
  {
    test: /^\/tide\/analytics(\/|$)/,
    meta: {
      icon: faChartArea,
      title: "Analytics",
      withDates: true,
      isSingleSite: true,
    },
  },
  {
    test: /^\/tide\/dashboard(\/|$)/,
    meta: {
      icon: faChartPie,
      title: "Dashboard",
      withDates: true,
      isSingleSite: true,
    },
  },
  {
    test: /^\/tide\/feeding-strategies(\/|$)/,
    meta: {
      icon: faProjectDiagram,
      title: "Feed Automation",
      withDates: false,
      isSingleSite: true,
    },
  },
  {
    test: /^\/tide\/automation-form(\/|$)/,
    meta: {
      icon: faSitemap,
      title: "Automation",
      withDates: false,
      isSingleSite: true,
    },
  },
  {
    test: /^\/tide\/feeding(\/|$)/,
    meta: {
      icon: faList,
      title: "Feeding",
      withDates: false,
      isSingleSite: true,
    },
  },
  {
    test: /^\/tide\/video(\/|$)/,
    meta: { icon: faVideo, title: "Video", hide: true },
  },
  // Marlin v2 MFE — temporary unconditional routes (permission gating disabled while wiring up)
  {
    test: /^\/marlin-v2\/video(\/|$)/,
    meta: { icon: faVideo, title: "Video", hide: true },
  },
  {
    test: /^\/marlin-v2\/feeding(\/|$)/,
    meta: {
      icon: faList,
      title: "Feeding",
      withDates: false,
      isSingleSite: true,
    },
  },
  {
    test: /^\/marlin-v2\/admin-configuration(\/|$)/,
    meta: { icon: faCog, title: "Configuration", hide: true },
  },
  {
    test: /^\/live-feeding(\/|$)/,
    meta: {
      icon: faPlay,
      title: "Live Feeding",
      withDates: false,
      isSingleSite: false,
    },
  },
  {
    test: /^\/camera-automation(\/|$)/,
    meta: {
      icon: faCamera,
      title: "Camera Automation",
      withDates: true,
      isSingleSite: true,
    },
  },
  {
    test: /^\/admin-configuration(\/|$)/,
    meta: { icon: faCog, title: "Admin Configuration", hide: true },
  },
  // Akva
  {
    test: /^\/akva-analytics(\/|$)/,
    meta: {
      icon: faFish,
      title: "Feeding Analytics",
      withDates: true,
      isSingleSite: false,
    },
  },
  // Shell
  {
    test: /^\/test-mfe(\/|$)/,
    meta: { icon: faGlobe, title: "Test MFE", hide: true },
  },
];

export function metaForPath(pathname: string): ToolbarMeta | null {
  const match = STATIC_ROUTE_META.find((entry) => entry.test.test(pathname));
  return match?.meta ?? null;
}
